// src/hooks/useChat.js - Complete File with Dashboard Data Refresh
import { useState, useCallback, useRef, useEffect } from 'react';
import apiService from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { previewThemeToken } from '../theme';

const API_BASE = '';

export const useChat = (onDataChanged) => {
  const { user } = useAuth();
  const { effective: effectiveTheme, refreshBranding } = useTheme() || {};
  const [messages, setMessages] = useState([]);
  const messagesRef = useRef([]);
  // Debug: Track messages changes
  useEffect(() => {
    console.log("Messages state updated:", messages.length, "messages");
    messagesRef.current = messages;
    console.log("Messages content:", messages);
  }, [messages]);
  const [isLoading, setIsLoading] = useState(false);
  const [pendingConfirmation, setPendingConfirmation] = useState(null);
  const [isWaitingForConfirmation, setIsWaitingForConfirmation] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [allHistory, setAllHistory] = useState([]);
  const messagesEndRef = useRef(null);

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

   useEffect(() => {
  if (messagesEndRef.current) {
    const container = messagesEndRef.current;
    const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
    
    // Only auto-scroll if already near bottom (for new messages)
    // Don't auto-scroll if user has scrolled up to read history
    if (isNearBottom) {
      container.scrollTop = container.scrollHeight;
    }
  }
}, [messages, isLoading]);

  // Start with empty chat - no automatic history loading
  useEffect(() => {
    console.log('💬 Chat initialized - starting fresh');
    setMessages([]);
  }, []);

// Fixed loadChatHistory - reverse EACH batch from backend
const loadChatHistory = useCallback(async (maxMessages = 10, append = false) => {
  try {
    console.log('🔄 Loading chat history...');
    console.log("Current messages count:", messagesRef.current.length);

    // Get container for scroll management
    const messagesContainer = document.querySelector('.chat-messages') ||
                            document.querySelector('.messages-container') ||
                            document.querySelector('[class*="message"]')?.parentElement;

    const scrollHeightBefore = messagesContainer?.scrollHeight || 0;
    const scrollTopBefore = messagesContainer?.scrollTop || 0;

    const sessionId = localStorage.getItem('chatSessionId') || 'default';
    
    // Calculate offset for pagination
    const offset = append ? messagesRef.current.length : 0;
    
    console.log(`📊 Fetching: offset=${offset}, limit=${maxMessages}`);

    const data = await apiService.request(
      `/api/chat/history?sessionId=${sessionId}&limit=${maxMessages}&offset=${offset}`
    );

    const history = data.history || [];
    console.log(`📜 Received ${history.length} messages from backend`);
    
    // Transform messages
    const transformedHistory = Array.isArray(history) ? history
      .map(msg => ({
        role: msg.role === 'user' ? 'user' : 'assistant',
        content: msg.content || '',
        timestamp: msg.timestamp,
        id: msg.id || `hist-${Date.now()}-${Math.random()}`,
        sender: msg.role === 'user' ? 'user' : 'Aetheris',
        isHistorical: true
      })) : [];

    // FIX: Backend returns each batch with newest first WITHIN that batch
    // So we need to reverse each batch to get chronological order

    if (append) {
      // When appending older messages, they go at the BEGINNING
      setMessages(prev => {
        console.log(`➕ Adding ${transformedHistory.length} older messages to beginning`);
        return [...transformedHistory, ...prev];
      });
      
      setAllHistory(prev => [...transformedHistory, ...prev]);
      
    // REPLACED the "Maintain scroll position" block with this:
setTimeout(() => {
  // Simply find the first scrollable div in the chat panel
  const chatPanel = document.querySelector('[style*="position: fixed"][style*="bottom: 100px"]');
  if (!chatPanel) return;
  
  // Get all scrollable elements
  const scrollables = Array.from(chatPanel.querySelectorAll('div')).filter(el => {
    return el.scrollHeight > el.clientHeight;
  });
  
  // The message container is usually the first or largest scrollable element
  const messageContainer = scrollables[0]; // Just take the first one
  
  if (messageContainer) {
    messageContainer.scrollTop = 0;
    console.log('📍 Scrolled to top');
  } else {
    console.log('❌ No scrollable container found');
  }
}, 300);

} else {
      // Initial load - also reverse for chronological order
      setMessages(transformedHistory);
      setAllHistory(transformedHistory);
      console.log(`✅ Initial load: ${transformedHistory.length} messages`);

      // Scroll to bottom to show most recent
      setTimeout(() => {
        if (messagesContainer) {
         messagesContainer.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          console.log(`📍 Scrolled to top to show new messages`);
        }
      }, 50);
    }

    messagesRef.current = append ? [...transformedHistory, ...messagesRef.current] : transformedHistory;
    setShowHistory(true);
    
    return transformedHistory;

  } catch (error) {
    console.error('❌ Failed to load chat history:', error);
    return [];
  }
}, [API_BASE]);

  // Clear all messages and start fresh
  const startFreshChat = useCallback(() => {
    setMessages([]);
    setAllHistory([]);
    setShowHistory(false);
    setPendingConfirmation(null);
    setIsWaitingForConfirmation(false);
    console.log('🆕 Started fresh chat');
  }, []);

  // Send message to chat API
  const sendMessage = useCallback(async (messageText, isConfirmation = false) => {
    if (!messageText.trim() && !isConfirmation) return;

    console.log('📤 Sending message:', messageText);

    const userMessage = {
      role: 'user',
      content: messageText,
      timestamp: new Date().toISOString(),
      id: Date.now(),
      sender: 'user'
    };

    // Add user message to chat immediately
    if (!isConfirmation) {
      setMessages(prev => [...prev, userMessage]);
    }
    setIsLoading(true);

    // Get or create sessionId to match file uploads
    const sessionId = localStorage.getItem('chatSessionId') ||
                      'session-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
    if (!localStorage.getItem('chatSessionId')) {
      localStorage.setItem('chatSessionId', sessionId);
    }

    console.log("Current messages being sent:", messagesRef.current);
    console.log("Using sessionId:", sessionId);

    try {
      console.log("Messages in state:", messagesRef.current.length, "Messages in ref:", messagesRef.current.length);
      const response = await apiService.request('/api/chat', {
        method: 'POST',
      body: JSON.stringify({
        message: messageText,
        userId: user?._id,
        companyId: user?.businessId,
        sessionId: sessionId,
        conversationHistory: messagesRef.current.slice(-10) || []
      })
    });

if (response.error) {
  throw new Error(response.error);
}

      // Aetheris proposing a theme color change - preview it live immediately
      // (same CSS-var mechanism the Customization page uses), before the user
      // has said "yes". If they say "no" (or the proposal fails), requiresThemeResync
      // below discards this preview and restores the real saved theme.
      if (response.themePreview?.tokenKey && response.themePreview?.hex && effectiveTheme) {
        previewThemeToken(effectiveTheme, response.themePreview.tokenKey, response.themePreview.hex);
      }
      // A change_theme confirmation was just resolved (approved or rejected) -
      // resync every themed CSS var to the true, current saved state either way.
      if (response.requiresThemeResync) {
        refreshBranding?.();
      }

      console.log('🔥 Chat response:', response);
      console.log('🔍 Checking for dataChanges:', response.dataChanges);
      console.log('🔍 DataChanges length:', response.dataChanges?.length);

      // NEW: Check for data changes and trigger dashboard refresh
      if (response.dataChanges && response.dataChanges.length > 0) {
        console.log('📊 Data changes detected:', response.dataChanges);

        // Notify dashboard to refresh with a small delay to ensure backend save is complete
        if (onDataChanged) {
          setTimeout(() => {
            console.log('🔄 Triggering dashboard refresh...');
            onDataChanged();
          }, 100);
        }
      }

      // Handle confirmation requests
      if (response.requiresConfirmation) {
        setPendingConfirmation(response.confirmationData);
        setIsWaitingForConfirmation(true);

        const confirmationMessage = {
          role: 'assistant',
          content: response.response || response.message,
          timestamp: new Date().toISOString(),
          id: Date.now() + 1,
          requiresConfirmation: true,
          sender: 'aetheris'
        };

        setMessages(prev => [...prev, confirmationMessage]);
      } else {
        // Regular response - handle both response and message fields
        const responseText = response.response || response.message || 'No response received';

        const assistantMessage = {
          role: 'assistant',
          content: responseText,
          timestamp: response.timestamp || new Date().toISOString(),
          id: Date.now() + 1,
          sender: 'aetheris',
          coresUsed: response.coresUsed || [],
          coreName: response.coreName || null,
          newModule: response.newModule || null,
          dataChanges: response.dataChanges || [], // Include data changes info for debugging
          image: response.image || null,
          imageSource: response.imageSource || null,
          imageTitle: response.imageTitle || null
        };

        setMessages(prev => [...prev, assistantMessage]);

        // Clear any pending confirmations
        setPendingConfirmation(null);
        setIsWaitingForConfirmation(false);
      }

      return response;

    } catch (error) {
      console.error('❌ Chat error:', error);

      const errorMessage = {
        role: 'assistant',
        content: `Sorry, I encountered an error: ${error.message}. Please check that your backend server is running on port 3000.`,
        timestamp: new Date().toISOString(),
        id: Date.now() + 1,
        error: true,
        sender: 'aetheris'
      };

      setMessages(prev => [...prev, errorMessage]);
      return { error: error.message };

    } finally {
      setIsLoading(false);
    }
  }, [onDataChanged, effectiveTheme, refreshBranding]);

  // Handle confirmation responses
  const handleConfirmation = useCallback(async (confirmed) => {
    if (!pendingConfirmation) return;

    const confirmationText = confirmed ? 'yes' : 'no';

    // Add user's confirmation to chat
    const confirmationMessage = {
      role: 'user',
      content: `Confirmation: ${confirmationText}`,
      timestamp: new Date().toISOString(),
      id: Date.now(),
      isConfirmation: true,
      sender: 'user'
    };

    setMessages(prev => [...prev, confirmationMessage]);

    // Send confirmation to backend
    const result = await sendMessage(confirmationText, true);

    // Clear confirmation state
    setPendingConfirmation(null);
    setIsWaitingForConfirmation(false);

    return result;
  }, [pendingConfirmation, sendMessage]);

  // Cancel pending confirmation
  const cancelConfirmation = useCallback(() => {
    setPendingConfirmation(null);
    setIsWaitingForConfirmation(false);

    const cancelMessage = {
      role: 'assistant',
      content: 'Action cancelled.',
      timestamp: new Date().toISOString(),
      id: Date.now(),
      sender: 'aetheris'
    };

    setMessages(prev => [...prev, cancelMessage]);
  }, []);

  // Retry last message
  const retryLastMessage = useCallback(() => {
    const lastUserMessage = messages
      .slice()
      .reverse()
      .find(msg => msg.sender === 'user');

    if (lastUserMessage) {
      console.log('🔄 Retrying last message:', lastUserMessage.content);
      return sendMessage(lastUserMessage.content);
    }
  }, [messages, sendMessage]);

  // Test connection
  const testConnection = useCallback(async () => {
    try {
      console.log("Messages in state:", messagesRef.current.length, "Messages in ref:", messagesRef.current.length);
      const response = await fetch(`${API_BASE}/api/health`);
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Backend connection test successful:', data);
        return { success: true, data };
      } else {
        throw new Error(`HTTP ${response.status}`);
      }
    } catch (error) {
      console.error('❌ Backend connection test failed:', error);
      return { success: false, error: error.message };
    }
  }, []);

  return {
    messages,
    setMessages,
    isLoading,
    pendingConfirmation,
    isWaitingForConfirmation,
    messagesEndRef,
    showHistory,
    allHistory,
    sendMessage,
    handleConfirmation,
    cancelConfirmation,
    loadChatHistory,
    startFreshChat,
    retryLastMessage,
    testConnection,
    clearChat: startFreshChat // Alias for backward compatibility
  };
};
